import type { Message, ChatContext, AIModel } from '../../../types/models';
import { APIError } from '../../error/errorHandler';
import { usageService } from '../../../services/UsageService';
import { useStore } from '../../../store/useStore';
import { getProviderConfig, getProviderUrl, getProviderEndpoint, getModelInfo, getModelsByProvider } from '../../../config/modelsDatabase';

interface CerebrasResponse {
  id: string;
  choices: Array<{
    finish_reason: string;
    index: number;
    message: {
      content: string;
      role: string;
    };
  }>;
  created: number;
  model: string;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export async function getModels(): Promise<AIModel[]> {
  try {
    console.log('Getting Cerebras models from database...');
    // Get all models from database where provider is 'cerebras'
    const models = getModelsByProvider('cerebras');
    return models.map(model => ({
      id: model.id,
      name: model.name,
      provider: model.provider,
      type: model.type,
      icon: model.icon,
      description: model.description,
      isConfigured: true,
      systemApiCost: (model.pricing.prompt_tokens_per_1k + model.pricing.completion_tokens_per_1k) / 2
    }));
  } catch (error) {
    console.error('Error getting Cerebras models:', error);
    throw error;
  }
}

export async function sendMessage(
  message: string,
  apiKey: string,
  modelId: string,
  context: ChatContext,
  previousMessages: Message[]
): Promise<string> {
  try {
    // Get Cerebras configuration
    const config = getProviderConfig('cerebras');
    if (!config) {
      throw new Error('Cerebras provider configuration not found');
    }

    const chatEndpoint = getProviderEndpoint('cerebras', 'chat');
    if (!chatEndpoint) {
      throw new Error('Cerebras chat endpoint not found');
    }

    // Get model information
    const model = getModelInfo(modelId);
    if (!model) {
      throw new Error(`Invalid model ID: ${modelId}`);
    }

    const messages = previousMessages.map(msg => ({
      role: msg.role,
      content: msg.content
    }));

    // Add the new message
    messages.push({
      role: 'user',
      content: message
    });

    // Add system message if present
    if (context.systemPrompt) {
      messages.unshift({
        role: 'system',
        content: context.systemPrompt
      });
    }

    const response = await fetch(getProviderUrl('cerebras', chatEndpoint), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        ...config.headers
      },
      body: JSON.stringify({
        model: modelId.toLowerCase(),
        messages: messages,
        stream: false,
        temperature: 0,
        max_completion_tokens: -1,
        seed: 0,
        top_p: 1
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get response from Cerebras');
    }

    const data = await response.json() as CerebrasResponse;

    // Record usage if available in the response
    if (data.usage) {
      console.log('Cerebras API Response:', data);
      
      // Calculate costs based on model pricing
      const inputCost = (data.usage.prompt_tokens / 1000) * model.pricing.prompt_tokens_per_1k;
      const outputCost = (data.usage.completion_tokens / 1000) * model.pricing.completion_tokens_per_1k;
      
      console.log('Recording Cerebras usage:', {
        modelId,
        usage: {
          prompt_tokens: data.usage.prompt_tokens,
          completion_tokens: data.usage.completion_tokens,
          total_tokens: data.usage.total_tokens,
          inputCost,
          outputCost,
          totalCost: inputCost + outputCost
        }
      });

      const { user, topUpValue } = useStore.getState();
      if (user) {
        await usageService.recordUsage(
          user.uid,
          modelId,
          {
            prompt_tokens: data.usage.prompt_tokens,
            completion_tokens: data.usage.completion_tokens,
            total_tokens: data.usage.total_tokens
          },
          topUpValue
        );
      }
    } else {
      console.warn('No usage data in Cerebras response:', data);
    }

    return data.choices[0].message.content;
  } catch (error: any) {
    console.error('Error:', error);
    if (error.message.includes('API key')) {
      throw new Error('Invalid or missing Cerebras API key');
    }
    throw new APIError(
      error.message || 'Failed to communicate with Cerebras API',
      'Cerebras'
    );
  }
}